import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card.js';
import { useBrandingStore } from '@/stores/branding.js';

/**
 * Shared frame for every auth page (login / register / forgot). Desktop shows
 * the brand hero beside the card; mobile shows a compact logo above it. Pages
 * pass only their card body as children so all three stay visually identical.
 *
 * `compact` tightens the card's padding and internal spacing for the one page
 * that has to fit a whole registration form and its submit button on a phone
 * screen without scrolling; login and forgot keep the roomier default.
 */
export function AuthShell({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  const { t } = useTranslation('auth');
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  const initial = sitename.slice(0, 1).toUpperCase() || 'B';

  return (
    <div className="w-full min-h-dvh mx-auto max-w-7xl px-6 lg:px-12 py-8 grid lg:grid-cols-[1.1fr_minmax(0,440px)] gap-10 lg:gap-16 items-center">
      <aside className="hidden lg:flex flex-col gap-8 pr-6">
        <Link to="/" className="flex items-center gap-3 self-start">
          <div className="w-16 h-16 rounded-2xl bg-[var(--primary)] grid place-items-center font-extrabold text-white text-3xl shadow-[0_10px_30px_var(--primary-glow)]">
            {initial}
          </div>
          <span className="font-extrabold text-4xl tracking-tight">{sitename}</span>
        </Link>
        <div className="flex flex-col gap-4">
          <h2 className="text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight">
            {t('login.heroHeadline', { sitename })}
          </h2>
          <p className="text-base xl:text-lg text-[var(--text-secondary)] max-w-md leading-relaxed">
            {t('login.heroSubtitle')}
          </p>
        </div>
        <ul className="flex flex-col gap-3 text-sm text-[var(--text-secondary)]">
          <li className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
            {t('login.heroFeature1')}
          </li>
          <li className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
            {t('login.heroFeature2')}
          </li>
          <li className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
            {t('login.heroFeature3')}
          </li>
        </ul>
      </aside>

      <div className="w-full max-w-md lg:max-w-none mx-auto lg:mx-0 flex flex-col gap-6">
        <Link to="/" className="lg:hidden self-center flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[var(--primary)] grid place-items-center font-extrabold text-white text-2xl shadow-[0_8px_24px_var(--primary-glow)]">
            {initial}
          </div>
          <span className="font-extrabold text-2xl tracking-tight">{sitename}</span>
        </Link>

        <Card
          className={
            compact
              ? 'p-5 sm:p-7 md:p-8 flex flex-col gap-4'
              : 'p-8 md:p-10 flex flex-col gap-6'
          }
        >
          {children}
        </Card>
      </div>
    </div>
  );
}
